import React, { useEffect, useState } from 'react';
import { Server, Plus, RefreshCw, Trash2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Zap, Pencil } from 'lucide-react';
import api from '../../services/api';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Toggle } from '../../components/ui/Toggle';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import alertService from '../../utils/alert';

const DISCOVERY_BADGE = {
  idle:        'slate',
  discovering: 'warning',
  ready:       'success',
  failed:      'danger',
};

const BLANK = { name: '', serverUrl: '', authType: 'none', authKeyName: '', authValue: '' };

export default function McpConnectorsPage() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // connector being edited
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});
  const [planError, setPlanError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/support/mcp-connectors');
      setConnectors(data.data || []);
    } catch (err) {
      // A 403 here means the company's plan doesn't include the
      // agentic_support_widget feature this route is gated on — this used
      // to throw as an unhandled rejection (no catch at all) and leave the
      // page silently showing "No MCP connectors yet", which reads as "you
      // have zero connectors" rather than "you don't have access at all".
      if (err.response?.status === 403) {
        setPlanError(err.response.data?.message || err.response.data?.error || 'This feature is not available on your current plan.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = key => val => setForm(f => ({ ...f, [key]: val }));

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, serverUrl: c.serverUrl, authType: c.authType, authKeyName: c.authKeyName || '', authValue: '' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.serverUrl.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/support/mcp-connectors/${editing._id}`, form);
      } else {
        await api.post('/support/mcp-connectors', form);
      }
      setShowModal(false);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this MCP connector?', 'danger', 'Delete'))) return;
    await api.delete(`/support/mcp-connectors/${id}`);
    load();
  };

  const discover = async (connector) => {
    setDiscovering(connector._id);
    try {
      await api.post(`/support/mcp-connectors/${connector._id}/discover`);
      // Poll for discovery to complete
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const { data } = await api.get('/support/mcp-connectors');
        const fresh = (data.data || []).find(c => c._id === connector._id);
        if (!fresh || fresh.discoveryStatus !== 'discovering' || attempts > 20) {
          clearInterval(poll);
          setDiscovering(null);
          setConnectors(data.data || []);
        }
      }, 1500);
    } catch {
      setDiscovering(null);
    }
  };

  const toggleTool = async (connector, toolName, enabled) => {
    const tools = connector.tools.map(t => t.name === toolName ? { ...t, enabled } : t);
    await api.put(`/support/mcp-connectors/${connector._id}`, { tools });
    load();
  };

  const toggleActive = async (connector) => {
    await api.put(`/support/mcp-connectors/${connector._id}`, { isActive: !connector.isActive });
    load();
  };

  if (planError) {
    return (
      <FeatureLockedPage
        title="MCP Connectors"
        subtitle="Connect Model Context Protocol servers — their tools are exposed to the support AI agent."
        icon={Server}
        featureName="MCP Connectors"
        description={planError}
      />
    );
  }

  return (
    <DashboardModuleShell
      title="MCP Connectors"
      subtitle="Connect Model Context Protocol servers — their tools are exposed to the support AI agent."
      icon={Server}
      actions={
        <Button variant="brand" icon={Plus} onClick={openCreate}>New Connector</Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64 text-[#706E6B] text-[13px]">Loading…</div>
      ) : connectors.length === 0 ? (
        <Card className="py-16 text-center">
          <Server size={40} className="mx-auto mb-3 text-[#D8DDE6]" />
          <p className="font-bold text-[#080707]">No MCP connectors yet</p>
          <p className="text-[12px] text-[#706E6B] mt-1">Add a connector to expose its tools to the support agent.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {connectors.map(c => (
            <Card key={c._id} className="p-0 overflow-hidden">
              {/* Header row */}
              <div className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-[13px] text-[#080707]">{c.name}</p>
                    <Badge variant={DISCOVERY_BADGE[c.discoveryStatus] || DISCOVERY_BADGE.idle}>
                      {c.discoveryStatus === 'ready' ? `${c.tools?.length || 0} tools` : c.discoveryStatus}
                    </Badge>
                    {!c.isActive && <Badge variant="slate">inactive</Badge>}
                  </div>
                  <p className="text-[11px] text-[#706E6B] mt-0.5 truncate">{c.serverUrl}</p>
                  {c.discoveryError && <p className="text-[11px] text-[#BA0517] mt-0.5">{c.discoveryError}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => discover(c)}
                    disabled={discovering === c._id || c.discoveryStatus === 'discovering'}
                    title="Re-discover tools"
                    className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] rounded-[4px] disabled:opacity-50 transition-colors"
                  >
                    <RefreshCw size={14} className={(discovering === c._id || c.discoveryStatus === 'discovering') ? 'animate-spin' : ''} />
                  </button>
                  <button onClick={() => toggleActive(c)} title={c.isActive ? 'Disable' : 'Enable'} className="p-2 text-[#706E6B] hover:text-[#080707] rounded-[4px] transition-colors">
                    {c.isActive ? <ToggleRight size={18} className="text-[#2E844A]" /> : <ToggleLeft size={18} />}
                  </button>
                  <button onClick={() => openEdit(c)} className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] rounded-[4px] transition-colors" title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(c._id)} className="p-2 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] rounded-[4px] transition-colors" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Tools list */}
              {c.tools?.length > 0 && (
                <div className="border-t border-[#D8DDE6]">
                  <button
                    onClick={() => setExpandedTools(prev => ({ ...prev, [c._id]: !prev[c._id] }))}
                    className="flex items-center gap-2 px-4 py-2 text-[13px] font-semibold text-[#706E6B] hover:bg-[#F4F6F9] w-full text-left transition-colors"
                  >
                    {expandedTools[c._id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    {c.tools.filter(t => t.enabled).length}/{c.tools.length} tools enabled
                  </button>
                  {expandedTools[c._id] && (
                    <div className="px-4 pb-3 space-y-1.5">
                      {c.tools.map(tool => (
                        <div key={tool.name} className="flex items-start justify-between gap-3 p-2.5 bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6]">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Zap size={11} className={tool.enabled ? 'text-[#0176D3]' : 'text-[#D8DDE6]'} />
                              <p className="text-[11px] font-mono font-bold text-[#080707]">{tool.name}</p>
                            </div>
                            {tool.description && <p className="text-[11px] text-[#706E6B] mt-0.5">{tool.description}</p>}
                          </div>
                          <Toggle
                            checked={!!tool.enabled}
                            onChange={enabled => toggleTool(c, tool.name, enabled)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit MCP Connector' : 'New MCP Connector'}
        icon={Server}
        maxWidth="max-w-lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              variant="brand"
              loading={saving}
              disabled={!form.name.trim() || !form.serverUrl.trim()}
              onClick={save}
            >
              {editing ? 'Save Changes' : 'Create & Discover'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Name *" value={form.name} onChange={e => set('name')(e.target.value)} />
          <Input
            label="Server URL *"
            value={form.serverUrl}
            onChange={e => set('serverUrl')(e.target.value)}
            placeholder="https://mcp.example.com/sse"
          />
          <div className="space-y-2">
            <label className="text-[12px] font-medium ml-0.5" style={{ color: 'var(--color-text-main)' }}>Auth Type</label>
            <select
              value={form.authType}
              onChange={e => set('authType')(e.target.value)}
              className="w-full px-4 h-9 border border-[#D8DDE6] rounded-[4px] text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
            >
              <option value="none">None</option>
              <option value="bearer">Bearer Token</option>
              <option value="header">Custom Header</option>
            </select>
          </div>
          {form.authType === 'header' && (
            <Input
              label="Header Name"
              value={form.authKeyName}
              onChange={e => set('authKeyName')(e.target.value)}
              placeholder="X-Api-Key"
            />
          )}
          {form.authType !== 'none' && (
            <Input
              label={editing ? 'New Secret (blank = keep existing)' : 'Secret / Token'}
              type="password"
              value={form.authValue}
              onChange={e => set('authValue')(e.target.value)}
            />
          )}
        </div>
      </Modal>
    </DashboardModuleShell>
  );
}
